<template>
  <protected :with="Permission.READ_STATISTICS" default-message>
    <div>
      <admin-view-header title="Statystyki" />

      <tabs :tabs="tabs" v-model="tab">
        <template #general>
          <loader :state="actions.global" without-loading>
            <p>
              Całkowita liczba odwiedzin:
              <strong>{{ global.total }}</strong>
            </p>
            <time-statistics
              title="Liczba odwiedzin (dziennie)"
              color="primary"
              :data="global.daily"
              @request="requestGlobalStatistics({ daily: $event })"
            />
            <time-statistics
              title="Liczba odwiedzin (miesięcznie)"
              color="primary2"
              :data="global.monthly"
              @request="requestGlobalStatistics({ monthly: $event })"
            />

            <template #error>
              <div class="grey-message">
                <icon name="fa-times-circle" />
                <p>Dane niedostępne z powodu braku połączenia z serwerem.</p>
              </div>
            </template>
          </loader>
        </template>

        <template #videos>
          <loader :state="[actions.videos, actions.videosRank]" without-loading>
            <p>
              Całkowita liczba wyświetleń materiałów:
              <strong>{{ videos.total }}</strong>
            </p>

            <table-statistics
              key="videos-table-statistics"
              class="is-fullwidth"
              title="Najpopularniejsze materiały"
              :header="['Tytuł', 'Wyświetlenia', 'Akcja']"
              :data="videosRank"
              :data-fields="['lookup', 'total']"
              @request="requestVideosRank"
              ip-distinct-switch
            >
              <template #lookup="{ lookup }">
                <span :class="{ 'has-text-grey-light': !lookup }">{{
                  lookup || "Materiał usunięty"
                }}</span>
              </template>

              <template v-slot="video">
                <td>
                  <icon
                    name="fa-chart-area"
                    class="icon-control"
                    @click="showVideoStatistics(video)"
                  />
                </td>
              </template>
            </table-statistics>

            <time-statistics
              title="Całkowita liczba wyświetleń materiałów (dziennie)"
              color="primary"
              :data="videos.daily"
              @request="requestVideosStatistics({ daily: $event })"
            />
            <time-statistics
              title="Całkowita liczba wyświetleń materiałów (miesięcznie)"
              color="primary2"
              :data="videos.monthly"
              @request="requestVideosStatistics({ monthly: $event })"
            />

            <template #error>
              <div class="grey-message">
                <icon name="fa-times-circle" />
                <p>Dane niedostępne z powodu braku połączenia z serwerem.</p>
              </div>
            </template>
          </loader>
        </template>

        <template #messages>
          <loader
            :state="[actions.messages, actions.messagesRank]"
            without-loading
          >
            <p>
              Całkowita liczba wyświetleń ogłoszeń:
              <strong>{{ messages.total }}</strong>
            </p>

            <table-statistics
              key="messages-table-statistics"
              class="is-fullwidth"
              title="Najpopularniejsze ogłoszenia"
              :header="['Tytuł', 'Wyświetlenia']"
              :data="messagesRank"
              :data-fields="['lookup', 'total']"
              @request="requestMessagesRank"
              ip-distinct-switch
            >
              <template #lookup="{ lookup }">
                <span :class="{ 'has-text-grey-light': !lookup }">{{
                  lookup || "Ogłoszenie usunięte"
                }}</span>
              </template>
            </table-statistics>

            <time-statistics
              title="Całkowita liczba wyświetleń ogłoszeń (dziennie)"
              color="primary"
              :data="messages.daily"
              @request="requestMessagesStatistics({ daily: $event })"
            />
            <time-statistics
              title="Całkowita liczba wyświetleń ogłoszeń (miesięcznie)"
              color="primary2"
              :data="messages.monthly"
              @request="requestMessagesStatistics({ monthly: $event })"
            />

            <template #error>
              <div class="grey-message">
                <icon name="fa-times-circle" />
                <p>Dane niedostępne z powodu braku połączenia z serwerem.</p>
              </div>
            </template>
          </loader>
        </template>

        <template #users>
          <loader :state="actions.usersRank" without-loading>
            <table-statistics
              key="users-by-ip-table-statistics"
              class="is-fullwidth"
              title="Użytkownicy (wg. adresów IP)"
              :header="['Adres IP', 'Wyświetlenia']"
              :data="users.byIp"
              :data-fields="['key', 'total']"
              @request="requestUsersRank({ ip: $event })"
            />

            <table-statistics
              key="users-by-user-agent-table-statistics"
              class="is-fullwidth"
              title="Użytkownicy (wg. identyfikatora UserAgent)"
              :header="['Identyfikator UserAgent', 'Wyświetlenia']"
              :data="users.byUserAgent"
              :data-fields="['key', 'total']"
              @request="requestUsersRank({ userAgent: $event })"
            />

            <template #error>
              <div class="grey-message">
                <icon name="fa-times-circle" />
                <p>Dane niedostępne z powodu braku połączenia z serwerem.</p>
              </div>
            </template>
          </loader>
        </template>

        <template #custom>
          <validable-form @submit="submitCustomRank">
            <label class="label">Zapytanie JSON</label>
            <control-code-editor
              v-model="customQuery"
              ref="customQueryEditor"
              language="json"
              :rules="customQueryModelValidator"
            />
            <button
              class="button is-success"
              :class="{ 'is-loading': isActionLoading('customQuery') }"
              :disabled="isActionLoading('customQuery')"
            >
              <icon name="fa-play" />
              <span>Wykonaj zapytanie</span>
            </button>
          </validable-form>

          <loader :state="actions.customQuery" without-loading>
            <table-statistics
              key="custom-table-statistics"
              class="is-fullwidth"
              :header="['Klucz', 'Obiekt', 'Liczba rekordów']"
              :data="customRank"
              :data-fields="['key', 'lookup', 'total']"
              @request="requestCustomRank"
            />

            <template #error>
              <div class="grey-message">
                <icon name="fa-times-circle" />
                <p>Brak danych</p>
              </div>
            </template>
          </loader>
        </template>
      </tabs>
    </div>
  </protected>
</template>

<script>
import { mapState } from "vuex";
import {
  REQUEST_GLOBAL_STATISTICS,
  REQUEST_VIDEOS_STATISTICS,
  REQUEST_VIDEOS_RANK,
  REQUEST_MESSAGES_STATISTICS,
  REQUEST_MESSAGES_RANK,
  REQUEST_USERS_RANK,
  REQUEST_CUSTOM_RANK
} from ":actions";

import get from "lodash.get";

import { Icon, Tabs, ValidableForm, ControlCodeEditor } from "%helpers";

import AdminViewHeader from "%admin/components/global/AdminViewHeader";
import TimeStatistics from "%admin/components/global/TimeStatistics";
import TableStatistics from "%admin/components/global/TableStatistics";

import VideoStatisticsModal from "%modals/VideoStatisticsModal";

import { Protected, WithDispatcherProgress } from "%mixins";

const CUSTOM_QUERY_ERRORS = {
  UNKNOWN_GROUPING_KEY: "Nieprawidłowy klucz agregacji",
  UNKNOWN_LOOKUP_KEY: "Nieprawidłowy klucz asocjacji",
  UNKNOWN_DISTINCT_KEY: "Nieprawidłowy klucz unifikacji",
  UNKNOWN_SORT_KEY: "Nieprawidłowy klucz sortowania",
  INVALID_QUERY: "Nieprawidłowe zapytanie"
};

export default {
  name: "admin-statistics",

  components: {
    Icon,
    Tabs,
    AdminViewHeader,
    TimeStatistics,
    TableStatistics,
    ValidableForm,
    ControlCodeEditor
  },

  mixins: [Protected, WithDispatcherProgress],

  data() {
    return {
      tab: "general",
      tabs: {
        general: {
          icon: "fa-eye",
          text: "Ogólne"
        },
        videos: {
          icon: "fa-video",
          text: "Filmy"
        },
        messages: {
          icon: "fa-newspaper",
          text: "Ogłoszenia"
        },
        users: {
          icon: "fa-users",
          text: "Użytkownicy"
        },
        custom: {
          icon: "fa-cogs",
          text: "Niestandardowe"
        }
      },

      customQuery: "{\n\n}",
      customQueryModelValidator: {
        required: true,
        is_json: {
          shape: {
            groupBy: {
              required: true,
              type: "String"
            },
            distinct: {
              type: "String"
            },
            date: {
              type: "String"
            },
            domain: {
              type: "String"
            },
            action: {
              type: "String"
            },
            address: {
              type: "String"
            },
            userAgent: {
              type: "String"
            },
            page: {
              type: "Number"
            },
            size: {
              type: "String"
            }
          }
        }
      }
    };
  },

  beforeMount() {
    this.requestGlobalStatistics({ total: true, daily: {}, monthly: {} });
  },

  computed: {
    ...mapState({
      global: state => state.statistics.global,
      videos: state => state.statistics.videos,
      videosRank: state => state.statistics.videosRank,
      messages: state => state.statistics.messages,
      messagesRank: state => state.statistics.messagesRank,
      users: state => state.statistics.users,
      customRank: state => state.statistics.customRank
    })
  },

  methods: {
    showVideoStatistics({ key, lookup }) {
      this.$emit("show-modal", {
        component: VideoStatisticsModal,
        props: {
          video: {
            id: key,
            title: lookup
          }
        }
      });
    },

    requestGlobalStatistics({ daily, monthly, total }) {
      this.dispatchWithProgress("global", REQUEST_GLOBAL_STATISTICS, {
        daily,
        monthly,
        total
      });
    },

    requestVideosStatistics({ daily, monthly, total }) {
      this.dispatchWithProgress("videos", REQUEST_VIDEOS_STATISTICS, {
        daily,
        monthly,
        total
      });
    },

    requestVideosRank({ page, distinct }) {
      this.dispatchWithProgress("videosRank", REQUEST_VIDEOS_RANK, {
        page,
        distinct,
        size: 10
      });
    },

    requestMessagesStatistics({ daily, monthly, total }) {
      this.dispatchWithProgress("messages", REQUEST_MESSAGES_STATISTICS, {
        daily,
        monthly,
        total
      });
    },

    requestMessagesRank({ page, distinct }) {
      this.dispatchWithProgress("messagesRank", REQUEST_MESSAGES_RANK, {
        page,
        distinct,
        size: 10
      });
    },

    requestUsersRank({ ip, userAgent }) {
      this.dispatchWithProgress("usersRank", REQUEST_USERS_RANK, {
        ip,
        userAgent
      });
    },

    submitCustomRank() {
      this.requestCustomRank({ page: 1 }).catch(({ response }) => {
        if (get(response, "data.code") === "INVALID_QUERY") {
          this.$refs.customQueryEditor.setErrors([
            CUSTOM_QUERY_ERRORS[response.data.payload[0]]
          ]);
        } else {
          this.$refs.customQueryEditor.setErrors([
            CUSTOM_QUERY_ERRORS.INVALID_QUERY
          ]);
        }
      });
    },

    requestCustomRank({ page }) {
      return this.dispatchWithProgress("customQuery", REQUEST_CUSTOM_RANK, {
        page,
        query: this.customQuery
      });
    }
  },

  watch: {
    tab(current) {
      switch (current) {
        case "general":
          this.requestGlobalStatistics({ daily: {}, monthly: {}, total: {} });
          return;

        case "videos":
          this.requestVideosStatistics({
            daily: {},
            monthly: {},
            total: {}
          });
          this.requestVideosRank({});
          return;

        case "messages":
          this.requestMessagesStatistics({
            daily: {},
            monthly: {},
            total: {}
          });
          this.requestMessagesRank({});

          return;

        case "users":
          this.requestUsersRank({
            ip: {},
            userAgent: {}
          });
      }
    }
  }
};
</script>

<style></style>
